import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { InputStyle } from 'module/ui/components/atoms/input/input';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
  PopoverSelect,
  type PopoverSelectGroup,
  type PopoverSelectOption,
} from '@rocketlane/ui';
import { PageTemplateSectionSource } from 'api/custom-section/types';
import { PageTemplateLite, PageType } from 'api/page-template/types';
import { keyBy } from 'lodash';
import {
  useGetAllSectionsInTemplatesQuery,
  useGetSuggestedSectionsForDerivedSectionQuery,
} from 'queries/templates/template-builder-queries';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CustomSectionNode } from '../../../../../types';

const TriggerContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-placeholder);
`;

const TriggerDivider = styled.div`
  width: 1px;
  height: 16px;
  margin: 0 var(--spacing-03);
  background-color: var(--field-border-default);
`;

const SelectTrigger = styled.button.attrs({
  className: 'custom-input',
  type: 'button',
})`
  ${InputStyle};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-03);
  cursor: pointer;

  > ${FontAwesomeIcon} {
    color: var(--icon-secondary);
  }
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Wrapper = styled(Form)`
  ${Form.Item} {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;
const sectionAsOption = (
  section: PageTemplateSectionSource,
): PopoverSelectOption => ({
  label: section.pageTemplateSectionTitle,
  value: section.pageTemplateSectionId,
});

const allSectionsAsGroups = (
  sections: PageTemplateSectionSource[],
): PopoverSelectGroup[] => {
  const groupsByTemplateId = new Map<number, PopoverSelectGroup>();

  sections.forEach((section) => {
    const existingGroup = groupsByTemplateId.get(section.pageTemplateId);

    if (existingGroup) {
      existingGroup.items.push(sectionAsOption(section));
    } else {
      groupsByTemplateId.set(section.pageTemplateId, {
        key: String(section.pageTemplateId),
        label: section.pageTemplateName,
        items: [sectionAsOption(section)],
      });
    }
  });

  return Array.from(groupsByTemplateId.values());
};

interface Props {
  node: CustomSectionNode;
  value: PageTemplateSectionSource[];
  onChange: (sources: PageTemplateSectionSource[]) => void;
  pageTemplateSources: PageTemplateLite[];
}

export const DerivedSectionSourcesSelector = ({
  node,
  value,
  onChange,
  pageTemplateSources,
}: Props) => {
  const selectorLabel = useTranslation(
    'company_page_templates.sections.sources.selector.label',
  );
  const selectorPlaceholder = useTranslation(
    'company_page_templates.sections.sources.selector.placeholder',
  );
  const suggestedBlocksLabel = useTranslation(
    'company_page_templates.sections.sources.selector.group.suggested_blocks',
  );
  const searchPlaceholder = useTranslation('search');
  const doneLabel = useTranslation('done');
  const templateIds = useMemo(
    () => pageTemplateSources.map((source) => source.templateId),
    [pageTemplateSources],
  );
  /* c8 ignore start - React Query hooks tested via integration tests */
  const { data: suggestedSections } =
    useGetSuggestedSectionsForDerivedSectionQuery(
      {
        templateIds,
        sectionName: node.attrs.title ?? '',
      },
      undefined,
      PageType.Company,
    );
  const { data: allSections } = useGetAllSectionsInTemplatesQuery(
    templateIds,
    undefined,
    PageType.Company,
  );
  /* c8 ignore stop */
  const sectionLookup = useMemo(
    () => ({
      ...keyBy(suggestedSections ?? [], 'pageTemplateSectionId'),
      ...keyBy(allSections ?? [], 'pageTemplateSectionId'),
    }),
    [suggestedSections, allSections],
  );
  const groups = useMemo<PopoverSelectGroup[]>(
    () => [
      {
        key: 'suggested',
        label: suggestedBlocksLabel,
        items: (suggestedSections ?? []).map(sectionAsOption),
      },
      ...allSectionsAsGroups(allSections ?? []),
    ],
    [allSections, suggestedBlocksLabel, suggestedSections],
  );

  const selected = useMemo(() => {
    return value.map(sectionAsOption);
  }, [value]);

  const onSourceChange = useCallback(
    (next: PopoverSelectOption[]) => {
      onChange(
        next
          .map(({ value }) => sectionLookup[value])
          .filter((section) => section != null),
      );
    },
    [onChange, sectionLookup],
  );

  return (
    <Wrapper layout="vertical">
      <Form.Item
        name="sources"
        label={
          <FormattedMessage id="company_page_templates.sections.sources.selector.label" />
        }
        valuePropName=""
        getValueFromEvent={() => {}}
      >
        <SelectorContainer>
          <PopoverSelect
            className="w-full"
            groups={groups}
            value={selected}
            onChange={(next) => onSourceChange(Array.isArray(next) ? next : [])}
            isMulti
            commitOnClose
            dropdownHeader={selectorLabel}
            renderTrigger={() => (
              <SelectTrigger aria-label={selectorLabel}>
                <TriggerContent>{selectorPlaceholder}</TriggerContent>
                <TriggerDivider />
                <FontAwesomeIcon icon={faChevronDown} size="xs" />
              </SelectTrigger>
            )}
            searchPlaceholder={searchPlaceholder}
            doneLabel={doneLabel}
            widthMatchTrigger
            getOptionDataCy={(option) => `select-option-${option.label}`}
          />
        </SelectorContainer>
      </Form.Item>
    </Wrapper>
  );
};
